export function CaseStudiesExamples() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Real-World Seller Financing Success Stories
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Learn from actual seller financing transactions and see how property owners maximized their returns
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="bg-teal-600 text-white px-4 py-2 rounded-full inline-block mb-4 text-sm font-bold">
              CASE STUDY #1
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              12-Unit Apartment Building in Phoenix, AZ
            </h3>
            
            <div className="mb-6">
              <img
                src="https://public.readdy.ai/ai/img_res/0695d5bb9dd8403b371fcd50b9d8646f.jpg"
                alt="12-unit apartment building in Phoenix Arizona"
                className="w-full h-64 object-cover object-top rounded-lg"
              />
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Property Value:</span>
                <span className="text-gray-900 font-bold">$1,800,000</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Sale Price (Premium):</span>
                <span className="text-teal-600 font-bold">$2,070,000 (+15%)</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Down Payment:</span>
                <span className="text-gray-900 font-bold">$414,000 (20%)</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Financed Amount:</span>
                <span className="text-gray-900 font-bold">$1,656,000</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Interest Rate:</span>
                <span className="text-gray-900 font-bold">7.5%</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Loan Term:</span>
                <span className="text-gray-900 font-bold">15 years</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Monthly Payment:</span>
                <span className="text-teal-600 font-bold">$15,358</span>
              </div>
            </div>

            <div className="bg-teal-50 rounded-lg p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Results:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Total Interest Income:</strong> $1,108,440 over 15 years</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Total Return:</strong> $3,178,440 (vs. $1,800,000 traditional sale)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Tax Savings:</strong> Deferred $180,000+ in capital gains taxes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Closing Time:</strong> 18 days (vs. 90+ days traditional)</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="bg-teal-600 text-white px-4 py-2 rounded-full inline-block mb-4 text-sm font-bold">
              CASE STUDY #2
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              24-Unit Complex in Atlanta, GA (As-Is Sale)
            </h3>
            
            <div className="mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=older%20twenty%20four%20unit%20apartment%20complex%20needing%20minor%20repairs%20in%20Atlanta%20Georgia%20with%20mature%20trees%20and%20parking%20lot%20value%20add%20opportunity%20property%20southern%20architecture%20overcast%20sky&width=800&height=500&seq=case-study-atlanta-complex&orientation=landscape"
                alt="24-unit apartment complex in Atlanta Georgia"
                className="w-full h-64 object-cover object-top rounded-lg"
              />
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Property Condition:</span>
                <span className="text-gray-900 font-bold">Needs $150K Repairs</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Sale Price (As-Is):</span>
                <span className="text-teal-600 font-bold">$3,200,000</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Down Payment:</span>
                <span className="text-gray-900 font-bold">$800,000 (25%)</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Financed Amount:</span>
                <span className="text-gray-900 font-bold">$2,400,000</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Interest Rate:</span>
                <span className="text-gray-900 font-bold">8%</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Loan Term:</span>
                <span className="text-gray-900 font-bold">10 years (balloon)</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">Monthly Payment:</span>
                <span className="text-teal-600 font-bold">$29,137</span>
              </div>
            </div>

            <div className="bg-teal-50 rounded-lg p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Results:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Avoided Repair Costs:</strong> Saved $150,000 in pre-sale repairs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Interest Income (10 years):</strong> $1,096,440</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Balloon Payment:</strong> $1,761,000 received in year 10</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Closing Time:</strong> 12 days (property sold as-is)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <div className="bg-teal-600 text-white px-4 py-2 rounded-full inline-block mb-6 text-sm font-bold">
            CASE STUDY #3
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            8-Unit Building in Denver, CO (Partial Seller Financing)
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=attractive%20eight%20unit%20apartment%20building%20in%20Denver%20Colorado%20with%20mountain%20views%20modern%20exterior%20well%20maintained%20property%20with%20landscaping%20Rocky%20Mountain%20backdrop%20clear%20blue%20sky&width=800&height=600&seq=case-study-denver-building&orientation=landscape"
                alt="8-unit apartment building in Denver Colorado"
                className="w-full h-80 object-cover object-top rounded-lg shadow-md"
              />
            </div>

            <div>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between pb-2 border-b border-gray-200">
                  <span className="text-gray-700 font-semibold">Sale Price:</span>
                  <span className="text-gray-900 font-bold">$1,600,000</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-200">
                  <span className="text-gray-700 font-semibold">Down Payment:</span>
                  <span className="text-gray-900 font-bold">$320,000 (20%)</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-200">
                  <span className="text-gray-700 font-semibold">Bank First Mortgage:</span>
                  <span className="text-gray-900 font-bold">$960,000 (60%)</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-200">
                  <span className="text-gray-700 font-semibold">Seller Second Mortgage:</span>
                  <span className="text-teal-600 font-bold">$320,000 (20%)</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-200">
                  <span className="text-gray-700 font-semibold">Seller Financing Rate:</span>
                  <span className="text-gray-900 font-bold">9%</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-200">
                  <span className="text-gray-700 font-semibold">Seller Loan Term:</span>
                  <span className="text-gray-900 font-bold">7 years</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-gray-200">
                  <span className="text-gray-700 font-semibold">Monthly Payment to Seller:</span>
                  <span className="text-teal-600 font-bold">$4,932</span>
                </div>
              </div>

              <div className="bg-teal-50 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Results:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Interest Income:</strong> $94,288 over 7 years</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Lower Risk:</strong> Bank vetted buyer's qualifications</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Faster Sale:</strong> Seller financing made deal possible</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Tax Benefits:</strong> Spread capital gains over 7 years</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Key Takeaways from These Success Stories</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3">Substantial Returns</h4>
              <p className="text-gray-300 leading-relaxed">
                All three sellers earned significantly more through seller financing than they would have with traditional sales
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3">Faster Closings</h4>
              <p className="text-gray-300 leading-relaxed">
                Transactions closed in 12-18 days instead of the typical 60-120 days with bank financing
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3">Flexible Solutions</h4>
              <p className="text-gray-300 leading-relaxed">
                Different structures (full financing, as-is, partial) allowed sellers to meet their specific goals
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}